/**
 * Fair Style Space — Playground canvas slots (Unit 5).
 * Visual: Figma `42:6256`.
 * Slots are computed from `type_id` (+ bag family) — the backend stores none.
 */

import { OUTFIT_TYPE_ID, isBagMainCategory } from '@/features/capsules/api/capsules.data'
import type { OutfitItem, OutfitItemRef } from '@/features/style-space/api'
import {
  fairStyleSpaceAccessoryKind,
  mapOutfitItemToWardrobeItem,
  parseFairStyleSpaceItemId,
  type FairStyleSpaceAccessoryKind,
  type FairStyleSpaceWardrobeItem,
} from './fair-style-space-wardrobe.data'

export const FAIR_STYLE_SPACE_CANVAS_SLOT_IDS = [
  'outerwear',
  'top',
  'bottom',
  'shoes',
  'bag',
  'acc1',
  'acc2',
  'acc3',
] as const

export type FairStyleSpaceCanvasSlotId =
  (typeof FAIR_STYLE_SPACE_CANVAS_SLOT_IDS)[number]

export type FairStyleSpaceCanvasSlots = Record<
  FairStyleSpaceCanvasSlotId,
  string | null
>

export type FairStyleSpaceCanvasSlotLayout =
  | 'outer'
  | 'top'
  | 'bottom'
  | 'shoes'
  | 'bag'
  | 'acc-a'
  | 'acc-b'
  | 'acc-c'

export interface FairStyleSpaceCanvasSlotDef {
  id: FairStyleSpaceCanvasSlotId
  layout: FairStyleSpaceCanvasSlotLayout
  /** Wardrobe category that fills this slot */
  category:
    | 'tops'
    | 'bottoms'
    | 'outerwear'
    | 'shoes'
    | 'bags'
    | 'accessories'
  labelKey: string
  /** Accessory slots only: the family drawn in the frame (hat · glasses · belt). */
  accessoryKind?: FairStyleSpaceAccessoryKind
}

export const FAIR_STYLE_SPACE_CANVAS_SLOT_DEFS: readonly FairStyleSpaceCanvasSlotDef[] =
  [
    { id: 'outerwear', layout: 'outer', category: 'outerwear', labelKey: 'outerwear' },
    { id: 'top', layout: 'top', category: 'tops', labelKey: 'top' },
    { id: 'bottom', layout: 'bottom', category: 'bottoms', labelKey: 'bottom' },
    { id: 'shoes', layout: 'shoes', category: 'shoes', labelKey: 'shoes' },
    { id: 'bag', layout: 'bag', category: 'bags', labelKey: 'bag' },
    { id: 'acc1', layout: 'acc-a', category: 'accessories', labelKey: 'hat', accessoryKind: 'hat' },
    { id: 'acc2', layout: 'acc-b', category: 'accessories', labelKey: 'eyewear', accessoryKind: 'eyewear' },
    { id: 'acc3', layout: 'acc-c', category: 'accessories', labelKey: 'belt', accessoryKind: 'belt' },
  ]

const ACCESSORY_SLOT_IDS: readonly FairStyleSpaceCanvasSlotId[] = ['acc1', 'acc2', 'acc3']

/** The accessory slot a piece belongs in: its own family's slot; `other` takes any free one. */
function accessorySlotFor(
  kind: FairStyleSpaceAccessoryKind | undefined,
  slots: FairStyleSpaceCanvasSlots,
  preferred: FairStyleSpaceCanvasSlotId | null = null,
): FairStyleSpaceCanvasSlotId | null {
  const own = FAIR_STYLE_SPACE_CANVAS_SLOT_DEFS.find(
    (def) => def.accessoryKind && def.accessoryKind === kind,
  )
  if (own) return own.id
  if (preferred && ACCESSORY_SLOT_IDS.includes(preferred)) return preferred
  return ACCESSORY_SLOT_IDS.find((id) => !slots[id]) ?? null
}

/**
 * Does this slot take the piece? A hat goes to the hat slot, glasses to the glasses slot, a
 * belt to the belt slot; an `other` accessory (scarf, gloves) fits any of the three.
 */
export function canvasSlotAcceptsItem(
  slotId: FairStyleSpaceCanvasSlotId,
  item: Pick<FairStyleSpaceWardrobeItem, 'category' | 'accessoryKind'>,
): boolean {
  const def = FAIR_STYLE_SPACE_CANVAS_SLOT_DEFS.find((entry) => entry.id === slotId)
  if (!def || def.category !== item.category) return false
  if (!def.accessoryKind) return true
  const kind = item.accessoryKind ?? 'other'
  return kind === 'other' || kind === def.accessoryKind
}

/** The slots a piece may land in — the hint when it was dropped somewhere else. */
export function canvasSlotsForItem(
  item: Pick<FairStyleSpaceWardrobeItem, 'category' | 'accessoryKind'>,
): FairStyleSpaceCanvasSlotId[] {
  return FAIR_STYLE_SPACE_CANVAS_SLOT_DEFS.filter((def) => canvasSlotAcceptsItem(def.id, item)).map(
    (def) => def.id,
  )
}

export function createEmptyCanvasSlots(): FairStyleSpaceCanvasSlots {
  return {
    outerwear: null,
    top: null,
    bottom: null,
    shoes: null,
    bag: null,
    acc1: null,
    acc2: null,
    acc3: null,
  }
}

export function isCanvasEmpty(slots: FairStyleSpaceCanvasSlots): boolean {
  return FAIR_STYLE_SPACE_CANVAS_SLOT_IDS.every((id) => !slots[id])
}

export function countFilledCanvasSlots(slots: FairStyleSpaceCanvasSlots): number {
  return FAIR_STYLE_SPACE_CANVAS_SLOT_IDS.filter((id) => Boolean(slots[id])).length
}

export function findWardrobeItemById(
  items: readonly FairStyleSpaceWardrobeItem[],
  id: string | null,
): FairStyleSpaceWardrobeItem | undefined {
  if (!id) return undefined
  return items.find((item) => item.id === id)
}

/* ——— outfit ↔ canvas ——— */

/** Canvas slot for one outfit row; accessories go to their family's slot, `other` to a free one. */
export function canvasSlotForOutfitItem(
  item: Pick<OutfitItem, 'typeId' | 'mainCategoryId'> & { name?: string | null },
  slots: FairStyleSpaceCanvasSlots,
): FairStyleSpaceCanvasSlotId | null {
  switch (item.typeId) {
    case OUTFIT_TYPE_ID.top:
    case OUTFIT_TYPE_ID.fullbody:
      return 'top'
    case OUTFIT_TYPE_ID.bottom:
      return 'bottom'
    case OUTFIT_TYPE_ID.shoes:
      return 'shoes'
    case OUTFIT_TYPE_ID.outwear:
      return 'outerwear'
    case OUTFIT_TYPE_ID.accessories:
      if (isBagMainCategory(item.mainCategoryId)) return slots.bag ? null : 'bag'
      return accessorySlotFor(
        fairStyleSpaceAccessoryKind({ mainCategoryId: item.mainCategoryId, name: item.name }),
        slots,
      )
    default:
      return null
  }
}

export interface FairStyleSpaceOutfitCanvas {
  slots: FairStyleSpaceCanvasSlots
  /** Every piece the look carries, catalogue ones included, so the canvas can draw it. */
  items: FairStyleSpaceWardrobeItem[]
}

/** `outfit.items[]` → the 8 slots + placeable items (§2.4, §2.2a). */
export function outfitItemsToCanvas(
  items:
    | readonly (OutfitItem & { variantId?: number | null; name?: string | null; brand?: string | null })[]
    | null
    | undefined,
): FairStyleSpaceOutfitCanvas {
  const slots = createEmptyCanvasSlots()
  const placed: FairStyleSpaceWardrobeItem[] = []
  for (const raw of items ?? []) {
    const slot = canvasSlotForOutfitItem(raw, slots)
    if (!slot || slots[slot]) continue
    const item = mapOutfitItemToWardrobeItem(raw, isBagMainCategory(raw.mainCategoryId))
    slots[slot] = item.id
    if (raw.typeId === OUTFIT_TYPE_ID.fullbody && !slots.bottom) slots.bottom = item.id
    placed.push(item)
  }
  return { slots, items: placed }
}

/** Canvas → `POST/PATCH /playground/outfits` items; the slot is not sent, the backend derives it. */
export function canvasSlotsToOutfitItems(slots: FairStyleSpaceCanvasSlots): OutfitItemRef[] {
  const refs: OutfitItemRef[] = []
  const seen = new Set<string>()
  for (const id of FAIR_STYLE_SPACE_CANVAS_SLOT_IDS) {
    const value = slots[id]
    if (!value || seen.has(value)) continue
    const parsed = parseFairStyleSpaceItemId(value)
    if (!parsed) continue
    seen.add(value)
    refs.push(
      parsed.kind === 'wardrobe'
        ? { wardrobeProductOverviewId: parsed.overviewId }
        : { productId: parsed.productId },
    )
  }
  return refs
}

/* ——— the first deal of a look option over the REAL wardrobe (deterministic per variant).
 * Shuffle / Replace are the stylist's actions (`POST /aira/actions`) — no random re-deal here. */

function itemsByCategory(
  items: readonly FairStyleSpaceWardrobeItem[],
  category: FairStyleSpaceWardrobeItem['category'],
): FairStyleSpaceWardrobeItem[] {
  return items.filter((item) => item.category === category)
}

function pickVariant(
  candidates: readonly FairStyleSpaceWardrobeItem[],
  offset: number,
): string | null {
  if (candidates.length === 0) return null
  return candidates[offset % candidates.length]?.id ?? null
}

/** Occasions that usually go without a layer; every second variant adds one anyway. */
const LIGHT_OCCASIONS: ReadonlySet<string> = new Set(['casual', 'date', 'vacation', 'sport'])

export function generateCanvasSlots(
  occasion: string | null,
  items: readonly FairStyleSpaceWardrobeItem[],
  variant = 0,
): FairStyleSpaceCanvasSlots {
  const next = createEmptyCanvasSlots()
  next.top = pickVariant(itemsByCategory(items, 'tops'), variant)
  const layered = !LIGHT_OCCASIONS.has(occasion ?? '') || variant % 2 === 1
  next.outerwear = layered ? pickVariant(itemsByCategory(items, 'outerwear'), variant) : null
  next.bottom = pickVariant(itemsByCategory(items, 'bottoms'), variant)
  next.shoes = pickVariant(itemsByCategory(items, 'shoes'), variant)
  next.bag = pickVariant(itemsByCategory(items, 'bags'), variant)
  next.acc1 = pickVariant(itemsByCategory(items, 'accessories'), variant)
  next.acc2 = null
  next.acc3 = null
  return next
}

export function placeItemOnCanvas(
  slots: FairStyleSpaceCanvasSlots,
  item: FairStyleSpaceWardrobeItem,
  selectedSlot: FairStyleSpaceCanvasSlotId | null = null,
): FairStyleSpaceCanvasSlots {
  const next = { ...slots }

  if (item.category === 'accessories') {
    if (ACCESSORY_SLOT_IDS.some((id) => next[id] === item.id)) {
      return next
    }
    // a hat / glasses / belt has its own slot; another accessory takes the tapped or a free one
    const target = accessorySlotFor(item.accessoryKind, next, selectedSlot) ?? 'acc1'
    next[target] = item.id
    return next
  }

  // a dress (fullbody) takes the top AND the bottom slot: both silhouettes go (designer's note)
  if (item.typeId === OUTFIT_TYPE_ID.fullbody) {
    next.top = item.id
    next.bottom = item.id
    return next
  }
  const slot = FAIR_STYLE_SPACE_CANVAS_SLOT_DEFS.find(
    (def) => def.category === item.category && !def.id.startsWith('acc'),
  )
  if (slot) {
    // a top or a bottom over a dress removes the dress (no confirm) — the other half goes too
    const current = next[slot.id]
    if (current && (slot.id === 'top' || slot.id === 'bottom')) {
      const other = slot.id === 'top' ? 'bottom' : 'top'
      if (next[other] === current) next[other] = null
    }
    next[slot.id] = item.id
  }
  return next
}

/** The piece in `top` is a dress spanning both slots. */
export function isFullbodyOnCanvas(slots: FairStyleSpaceCanvasSlots): boolean {
  return Boolean(slots.top) && slots.top === slots.bottom
}

export function formatFairStyleSpaceContextChip(
  occasionLabel: string,
  season: string,
): string {
  return `${occasionLabel} • ${season}`
}

/** Removing a dress from either slot removes it from both. */
export function clearCanvasSlotPair(
  slots: FairStyleSpaceCanvasSlots,
  slotId: FairStyleSpaceCanvasSlotId,
): FairStyleSpaceCanvasSlots {
  const next = clearCanvasSlot(slots, slotId)
  if ((slotId === 'top' || slotId === 'bottom') && slots.top && slots.top === slots.bottom) {
    next.top = null
    next.bottom = null
  }
  return next
}

export function clearCanvasSlot(
  slots: FairStyleSpaceCanvasSlots,
  slotId: FairStyleSpaceCanvasSlotId,
): FairStyleSpaceCanvasSlots {
  return { ...slots, [slotId]: null }
}
